import {
    getAoiRequirements,
    AoiRequirements,
    getPathRequirements,
    getPoiRequirements, 
    getEventRequirements,
    toPathCategories,
    toPathGpsCoordinates,
    toPaths,
    toPoiCategories,
    toEventPoiCategories,
    toPois,
    toAois,
    toAoiCategories,
    PathRequirements,
    PoiRequirements,
    EventRequirements,
    LocalizedToken,
    Path,
    Poi,
    Aoi,
    toAoiGpsCoordinates,
    filterDisplayed,
    getSettingsLanguges,
    SettingsLanguagesResponse
} from "@sodraberget/shared";
import { useDataStore } from "../stores/data.store";

const fetchData = (baseUrl: string) => {
    return new Promise<void>((resolve, reject) => {
        // Fetch core datasets.
        Promise.all([
            getPathRequirements(baseUrl),
            getPoiRequirements(baseUrl),
            getAoiRequirements(baseUrl),
            getSettingsLanguges(baseUrl),
        ])
        .then(([pathData, poiData, aoiData, languages]: [PathRequirements, PoiRequirements, AoiRequirements, SettingsLanguagesResponse[]]) => (
            // Events are optional; the map still loads if they cannot be fetched
            getEventRequirements(baseUrl, poiData.pois)
                .catch((error): EventRequirements => {
                    console.warn('Could not load events', error);
                    return { events: [], categories: [] };
                })
                .then((eventData) => [pathData, poiData, eventData, aoiData, languages] as const)
        ))
        .then((response) => {
            const [pathData, poiData, eventData, aoiData, languages] = response;
            const areas: {[key: string]: LocalizedToken} = {};

            const {mainContent, additionalMapContent} = useDataStore.getState();

            // Common data shared between both the Path and SnowmobilePath system
            const formattedPathCategories = toPathCategories(pathData.categories);
            const updatedCommonFields = {
                pathCategories: formattedPathCategories,
            };

            return {
                pathData,
                poiData,
                eventData,
                aoiData,
                languages,
                areas,
                mainContent,
                formattedPathCategories,
                updatedCommonFields,
            };
        })
        .then((data) => {
            const {
                pathData,
                poiData,
                eventData,
                aoiData,
                languages,
                areas,
                mainContent,
                updatedCommonFields,
            } = data;

            // Path specific data
            const formattedPaths = toPaths(filterDisplayed(pathData.paths));
            const formattedPathGpsCoordinates = toPathGpsCoordinates(pathData.paths);
            const updatedPathFields = {
                paths: formattedPaths,
                pathGpsCoordinates: formattedPathGpsCoordinates,
            };
    
            // Poi specific data (events are drawn as POIs with their own category namespace)
            const formattedPois = toPois(filterDisplayed([...poiData.pois, ...eventData.events]));
            const formattedPoiCategories = {
                ...toPoiCategories(poiData.categories),
                ...toEventPoiCategories(eventData.categories),
            };
            const updatedPoiFields = {
                pois: formattedPois,
                poiCategories: formattedPoiCategories,
            };

            // Aoi specific data
            const formattedAois = toAois(filterDisplayed(aoiData.aois));
            const formattedAoiCategories = toAoiCategories(aoiData.categories);  
            const formattedAoiGpsCoordinates = toAoiGpsCoordinates(aoiData.aois);
          
            const updatedAoiFields = {
                aois: formattedAois,
                aoiCategories: formattedAoiCategories,
                aoiGpsCoordinates: formattedAoiGpsCoordinates
            };

            // Add main content paths areas to used areas
            Object.values(formattedPaths).forEach((path: Path) => {
                if (mainContent.pathCategories?.length === 0 || mainContent.pathCategories?.includes(path.pathCategoryId)) {
                    path.areas.forEach((area: LocalizedToken) => {
                        const {id, name} = area;
                        areas[`${id}`] = {id, name};
                    });
                }
            });

            // Add main content poi areas to used areas
            Object.values(formattedPois).forEach((poi: Poi) => {
                if (mainContent.poiCategories?.length === 0 || mainContent.poiCategories?.includes(poi.poiCategoryId)) {
                    poi.areas.forEach((area: LocalizedToken) => {
                        const {id, name} = area;
                        areas[`${id}`] = {id, name};
                    });
                }
            });

            // Add main content aoi areas to used areas
            Object.values(formattedAois).forEach((aoi: Aoi) => {
                if (mainContent.aoiCategories?.length === 0 || mainContent.aoiCategories?.includes(aoi.aoiCategoryId)) {
                    aoi.areas.forEach((area: LocalizedToken) => {
                        const {id, name} = area;
                        areas[`${id}`] = {id, name};
                    });
                }
            });

            const updatedFields = {
                ...updatedCommonFields,
                ...updatedPathFields,
                ...updatedPoiFields,
                ...updatedAoiFields,
                ...languages,
                areas,
            };
            console.log('updating data with fields', updatedFields);

            // Set data in data store
            useDataStore.setState(() => updatedFields);

            resolve();
        })
        .catch((error) => reject(error));
    });
};

export { fetchData };